import { useEffect, useState } from "react";

import { PrivateBillEditSendToPortalPopupStrategy } from "@/Billing/Services/Portal/PrivateBillEditSendToPortalPopupStrategy";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { PrivateBillEditControl } from "@/Patient/Components/History/PrivateBillEditControl";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { IPatientHistoryPrivateBillsProps } from "./PrivateBillControl";

function PrivateBillEdit(props: IPatientHistoryPrivateBillsProps): JSX.Element {
    const isPortalEnabled: boolean = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;
    const isPortalInvitationFlowV2Enabled: boolean = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2;
    const privateBillModel: IPatientPrivateBillModel = props.Model;

    const isSaveLocked: boolean = privateBillModel.get("IsSaveLocked");
    const billNumber: string = privateBillModel.get("BillNumber");
    const billId: string = privateBillModel.get("PrivateBillId");
    const privateBill = privateBillModel.get("PrivateBillList")?.toArray().filter(x => x.Model.PrivateBillId == billId).firstOrDefault();

    const isFaxVisible: boolean = privateBillModel.get("SelectedBillBilledTo") == PrivateBill_BilledTo.ThirdParty;
    const isPrintVisible: boolean = cacheStore.ClinicSettings.EnablePrivateBillRecipient;
    const isPortalShown: boolean = cacheStore.ClinicSettings.EnablePrivateBillRecipient
        ? ((props.PatientModel?.IsRegisteredToPortal() || isPortalInvitationFlowV2Enabled) && isPortalEnabled)
        : ((props.PatientModel?.IsRegisteredToPortal() || isPortalInvitationFlowV2Enabled) && privateBill && isPortalEnabled);

    const phoneNumberRegex = new ValidationHelper()
        .getPhoneNumberValidationRule(cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId, null)
        .ValidationRegexPattern;

    const recipientName = privateBillModel.get("SelectedBillRecipientDetails")?.RecipientName;
    const faxNumber = privateBillModel.get("SelectedBillRecipientDetails")?.RecipientFaxNumber;
    const [isFaxNumberValid, setIsFaxNumberValid] = useState(ValidationHelper.isExactMatch(faxNumber, new RegExp(phoneNumberRegex)));

    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const [isSaveDisabled, setIsSaveDisabled] = useState(!isFormValid || isSaveLocked);

    const isFaxDisabled = !isFaxNumberValid || isSaveDisabled || IsNullOrWhiteSpace(recipientName);
    const isPrintDisabled = isSaveDisabled || (privateBillModel.get("SelectedBillBilledTo") == PrivateBill_BilledTo.ThirdParty && IsNullOrWhiteSpace(recipientName));
    const isPortalDisabled = cacheStore.ClinicSettings.EnablePrivateBillRecipient
        ? (isPrintDisabled)
        : (isSaveDisabled || IsNullOrWhiteSpace(billNumber));

    useEffect(() => {
        if (!privateBillModel.get("IsEditBill") && isArrayNullOrUndefinedOrEmpty(props.BillableServicesModel))
            sessionStore.dispatchAsync(LocalPatientOperations.History.LoadDummyServices);
    }, []);

    useEffect(() => {
        setIsSaveDisabled((!isFormValid || isSaveLocked));
    }, [props.BillableServicesModel.map(x => x.get("SelectedService")), isFormValid, isSaveLocked]);

    useEffect(() => {
        setIsFaxNumberValid(ValidationHelper.isExactMatch(faxNumber, new RegExp(phoneNumberRegex)));
    }, [faxNumber, phoneNumberRegex]);

    const isPaymentMethodPayable = privateBillModel.get("SelectedPaymentMethod") === BillingPaymentMethod.Payable;
    const isCloseButtonDisabled = isPaymentMethodPayable && cacheStore.ClinicSettings.EnablePrivateBillRecipient;
    const closeButton = (
        <ButtonControl
            className="btn btn-primary"
            onClick={() => onCloseBillClick()}
            disabled={isSaveDisabled || isCloseButtonDisabled || props.DisableButtons}
            label={cacheStore.GetString("CloseLabel")}
        />
    );

    const onCloseBillClick = (): void => {
        sessionStore.showPopup({
            title: cacheStore.GetString("CloseSelectedBillLabel"),
            content: cacheStore.GetString("CloseSelectedBillMessageLabel"),
            showCancel: true,
            okLabel: cacheStore.GetString("CloseLabel"),
            okAction: () => {
                const op = new LocalEvent(LocalPatientOperations.History.PrivateBill_OnSaveClick);
                op.AddItem("IsFromClose", true);
                LocalEventDispatcher.dispatch(op);
            },
            altButtonLabel: cacheStore.GetString("CloseAndPrintLabel"),
            altButtonAction: () => {
                const op = new LocalEvent(LocalPatientOperations.History.PrivateBill_OnSaveAndPrintClick);
                op.AddItem("IsFromClose", true);
                LocalEventDispatcher.dispatch(op);
            }
        });
    };

    const [isSyncToPortalPopupVisible, setIsSyncToPortalPopupVisible] = useState(false);
    const onPortalButtonClick = (): void => {
        if (!cacheStore.ClinicSettings.EnablePrivateBillRecipient || !IsNullOrWhiteSpace(billNumber)) {
            sessionStore.dispatch(LocalPatientOperations.History.PrivateBill_PortalStatusClicked_FromEdit, { bill: privateBill.Model });
            return;
        }
        {
            setIsSyncToPortalPopupVisible(true);
        }
    };

    const onCancelClick = (): void => {
        props.OnButtonClick(() => invokeOperation(LocalPatientOperations.History.PrivateBill_CancelNewBill));
    };
    const onSaveClick = (): void => {
        props.OnButtonClick(() => invokeOperation(LocalPatientOperations.History.PrivateBill_OnSaveClick));
    };
    const onPrintClick = (): void => {
        props.OnButtonClick(() => invokeOperation(LocalPatientOperations.History.PrivateBill_OnPrintClick));
    };
    const onFaxClick = (): void => {
        props.OnButtonClick(() => invokeOperation(LocalPatientOperations.History.PrivateBill_OnFaxClick));
    };

    const invokeOperation = (historyOperation: LocalPatientOperations.History): void => {
        sessionStore.dispatch(historyOperation);
    };

    return (
        <>
            {isSyncToPortalPopupVisible && (
                <SendToPortalPopup
                    model={PrivateBillEditSendToPortalPopupStrategy.getDefaultValueForSyncToPortalModel(
                        props.PatientModel.IndividualResponse.PortalSyncState,
                        props.PatientModel.IndividualResponse.Contact_id,
                        privateBillModel?.get("SelectedDateOfVisit")
                    )}
                    strategy={new PrivateBillEditSendToPortalPopupStrategy(
                        () => setIsSyncToPortalPopupVisible(false),
                        patientStore.getCurrentPatientContactId(),
                        false,
                        privateBillModel.get("SelectedPaymentMethod")
                    )}
                />
            )}
            <div className="summary-container">
                <div className="banner">
                    <h3 className="thin-font">{cacheStore.GetString("NewBillLabel")}</h3>
                </div>

                <PrivateBillEditControl
                    Model={privateBillModel}
                    BillableServicesModel={props.BillableServicesModel}
                    DateOfVisit_Set={LocalPatientOperations.History.PrivateBill_DateOfVisit_Set}
                    TodayClickedOp={LocalPatientOperations.History.PrivateBill_OnTodayClick}
                    DidMountOp={LocalPatientOperations.History.LoadDummyServices}
                    SelectClinician={LocalPatientOperations.History.PrivateBill_SelectClinician}
                    SiteSelectedOp={LocalPatientOperations.History.PrivateBill_SiteChange}
                    CostCenterChangedOp={LocalPatientOperations.History.PrivateBill_OnCostCenterChange}
                    PaymentMethodChangedOp={LocalPatientOperations.History.PrivateBill_OnSelectedPaymentChange}
                    DiscountChangedOp={LocalPatientOperations.History.PrivateBill_OnDiscountChange}
                    DiscountTypeChangeOp={LocalPatientOperations.History.PrivateBill_ChangeDiscountType}
                    ClearOp={LocalPatientOperations.History.PrivateBill_ClearSelection}
                    SelectService={LocalPatientOperations.History.PrivateBill_SelectBillableService}
                    PriceChange={LocalPatientOperations.History.PrivateBill_OnPriceChange}
                    QuantityChanged={LocalPatientOperations.History.PrivateBill_OnQuantityChange}
                    ChangeGST={LocalPatientOperations.History.PrivateBill_ChangeHasGST}
                    ChangeQST={LocalPatientOperations.History.PrivateBill_ChangeHasQST}
                    ChangeHST={LocalPatientOperations.History.PrivateBill_ChangeHasHST}
                    ChangeGenericTax={LocalPatientOperations.History.PrivateBill_ChangeHasGenericTax}
                    RemoveService={LocalPatientOperations.History.PrivateBill_RemoveService}
                    NotesChangeOp={LocalPatientOperations.History.PrivateBill_OnNotesChange}
                    OnCloseOperation={LocalPatientOperations.History.PrivateBill_CancelNewBill}
                    LockProviderOp={LocalPatientOperations.History.PrivateBill_LockProvider}
                    ChangeRecipientDetailsOp={LocalPatientOperations.History.PrivateBill_OnRecipientDetailsChange}
                    BilledToChangeOp={LocalPatientOperations.History.PrivateBill_Billing_OnBilledToChange}
                    onIsFormValidChange={setIsFormValid}
                />
                <div className="meta-footer padding-none-top padding-md-right flex-display flex-end flex-align-items-center">
                    <ButtonGroup>
                        <ButtonControl
                            className="btn btn-default"
                            onClick={onCancelClick}
                            label={cacheStore.GetString("CancelLabel")}
                            disabled={props.DisableButtons}
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            disabled={isSaveDisabled || props.DisableButtons}
                            onClick={onSaveClick}
                            label={cacheStore.GetString("SaveLabel")}
                            dataCy="privateBillEdit-save"
                        />

                        {isPrintVisible && (
                            <ButtonControl
                                className="btn btn-primary"
                                disabled={isPrintDisabled || props.DisableButtons}
                                onClick={onPrintClick}
                                label={cacheStore.GetString("PrintLabel")}
                            />
                        )}
                        {isFaxVisible && (
                            <ButtonControl
                                className="btn btn-primary"
                                disabled={isFaxDisabled || props.DisableButtons}
                                onClick={onFaxClick}
                                label={cacheStore.GetString("FaxLabel")}
                            />
                        )}

                        {closeButton}
                        {isPortalShown && (
                            <PortalStatusButton
                                disabled={isPortalDisabled || props.DisableButtons}
                                icon="share"
                                onClick={() => onPortalButtonClick()}
                            />
                        )}
                    </ButtonGroup>
                </div>
            </div>
        </>
    );
}

export { PrivateBillEdit };
